<template>
  <div class="dbv2">
    <!-- Hero Header -->
    <rmr-page-hero
      title="Pilotage"
      title-accent="Audience & Revenus"
      :icon="icons.mdiChartBoxOutline"
    >
      <template #subtitle>
        <p class="dbv2-update-line">
          {{ periodLabel }}
          <span v-if="lastUpdate" class="dbv2-update">
            &mdash; Mis a jour {{ lastUpdate }}
          </span>
        </p>
      </template>
      <template #actions>
        <div class="dbv2-hero-actions">
          <div class="dbv2-switch dbv2-switch--three">
            <button
              class="dbv2-switch-btn"
              :class="{ 'dbv2-switch-btn--active': currentView === 'month' }"
              @click="switchView('month')"
            >
              <v-icon small :color="currentView === 'month' ? RMR_INDIGO_DARK : RMR_ON_DARK_MUTED" class="mr-1">{{ icons.mdiCalendarMonth }}</v-icon>
              Mois
            </button>
            <button
              class="dbv2-switch-btn"
              :class="{ 'dbv2-switch-btn--active': currentView === 'year' }"
              @click="switchView('year')"
            >
              <v-icon small :color="currentView === 'year' ? RMR_INDIGO_DARK : RMR_ON_DARK_MUTED" class="mr-1">{{ icons.mdiCalendarText }}</v-icon>
              Année
            </button>
            <button
              class="dbv2-switch-btn"
              :class="{ 'dbv2-switch-btn--active': currentView === 'range' }"
              @click="switchView('range')"
            >
              <v-icon small :color="currentView === 'range' ? RMR_INDIGO_DARK : RMR_ON_DARK_MUTED" class="mr-1">{{ icons.mdiCalendarRange }}</v-icon>
              Plage
            </button>

            <div
              class="dbv2-switch-indicator dbv2-switch-indicator--three"
              :class="{
                'dbv2-switch-indicator--center': currentView === 'year',
                'dbv2-switch-indicator--right': currentView === 'range',
              }"
            ></div>
          </div>
        </div>
      </template>
    </rmr-page-hero>

    <!-- Filters Bar -->
    <div class="dbv2-filters">
      <v-card flat class="dbv2-filters-card">
        <div class="dbv2-filters-row">
          <!-- Month picker (month mode) -->
          <div v-if="currentView === 'month'" class="dbv2-filter-item">
            <label class="dbv2-filter-label">Mois</label>
            <rmr-date-picker-field
              :value="selectedMonthPicker"
              :display-value="periodLabel"
              :menu.sync="dateMenuFrom"
              type="month"
              close-on-input
              prepend-inner-icon="mdi-calendar-month"
              @input="onMonthPicked"
            />
          </div>

          <!-- Year picker (year mode) -->
          <div v-if="currentView === 'year'" class="dbv2-filter-item">
            <label class="dbv2-filter-label">Année</label>
            <rmr-select
              :value="selectedYear"
              :items="yearList"
              @change="onYearPicked"
              prepend-inner-icon="mdi-calendar-text"
            />
          </div>

          <!-- Range picker (plage mode) -->
          <div v-if="currentView === 'range'" class="dbv2-filter-item dbv2-filter-item--range">
            <label class="dbv2-filter-label">Période</label>
            <rmr-date-picker-field
              :key="rangePickerKey"
              :value="rangeDraft"
              :display-value="`${formattedDateFrom} — ${formattedDateTo}`"
              :menu.sync="dateMenuRange"
              range
              :picker-props="{ allowedDates: isRangeDateAllowed, max: todayStr }"
              prepend-inner-icon="mdi-calendar-range"
              @input="onRangePicked"
            />
          </div>

          <!-- Partner View Groups -->
          <div class="dbv2-filter-item dbv2-filter-grow">
            <label class="dbv2-filter-label">Groupe Partenaire</label>
            <v2-partner-view-group-filter
              store="dashboardV2"
              dense
              outlined
              hide-details
              placeholder="Tous les groupes"
            >
              <template v-slot:selection="{ index }">
                <span v-if="index === 0" class="dbv2-selection-count">
                  {{ localPartnerViewGroups.length }} sélectionné(s)
                </span>
              </template>
            </v2-partner-view-group-filter>
          </div>

          <!-- Site Groups -->
          <div class="dbv2-filter-item">
            <label class="dbv2-filter-label">Groupe de sites</label>
            <v2-site-group-filter
              store="dashboardV2"
              dense
              outlined
              hide-details
              placeholder="Tous"
            >
              <template v-slot:selection="{ index }">
                <span v-if="index === 0" class="dbv2-selection-count">
                  {{ localSiteGroups.length }} sélectionné(s)
                </span>
              </template>
            </v2-site-group-filter>
          </div>

          <!-- Sites -->
          <div class="dbv2-filter-item dbv2-filter-grow">
            <label class="dbv2-filter-label">Sites</label>
            <v2-site-filter
              store="dashboardV2"
              dense
              outlined
              hide-details
              placeholder="Tous les sites"
            >
              <template v-slot:selection="{ index }">
                <span v-if="index === 0" class="dbv2-selection-count">
                  {{ localSites.length }} sélectionné(s)
                </span>
              </template>
            </v2-site-filter>
          </div>

          <!-- Apply Button -->
          <div class="dbv2-filter-item dbv2-filter-action">
            <rmr-action-button
              :loading="isLoading"
              :disabled="isLoading"
              label="Actualiser"
              :icon="icons.mdiMagnify"
              variant="primary"
              @click="applyFilters"
            />
          </div>
        </div>

        <!-- Selected chips accumulated at bottom -->
        <div v-if="hasActiveSelections" class="dbv2-chips-row">
          <div class="dbv2-chips-label">Selections actives :</div>
          <div class="dbv2-chips-list">
            <v-chip
              v-for="group in selectedPartnerGroupChips"
              :key="'pg-' + group.id"
              small
              close
              class="dbv2-chip dbv2-chip--partner"
              @click:close="removePartnerViewGroup(group.id)"
            >
              {{ group.name }}
            </v-chip>
            <v-chip
              v-for="sg in selectedSiteGroupChips"
              :key="'sg-' + sg.id"
              small
              close
              class="dbv2-chip dbv2-chip--sitegroup"
              @click:close="removeSiteGroup(sg.id)"
            >
              {{ sg.name }}
            </v-chip>
            <v-chip
              v-for="site in selectedSiteChips"
              :key="'s-' + site.id"
              small
              close
              class="dbv2-chip dbv2-chip--site"
              @click:close="removeSite(site.id)"
            >
              {{ site.name }}
            </v-chip>
            <button
              v-if="totalSelections > 3"
              class="dbv2-chips-clear"
              @click="clearAllSelections"
            >
              Tout effacer
            </button>
          </div>
        </div>
      </v-card>
    </div>

    <!-- Tab Navigation -->
    <div class="dbv2-nav">
      <div class="dbv2-nav-bar">
        <rmr-tabs
          v-model="activeTab"
          :tabs="tabs"
          @input="onTabChange"
        />

        <div
          class="dbv2-switch dbv2-switch--two dbv2-switch--nav"
          role="group"
          aria-label="Source de trafic"
        >
          <button
            type="button"
            class="dbv2-switch-btn"
            :class="{ 'dbv2-switch-btn--active': trafficSource === 'wysistat' }"
            :aria-pressed="trafficSource === 'wysistat'"
            @click="switchTrafficSource('wysistat')"
          >
            Wysistat
          </button>
          <button
            type="button"
            class="dbv2-switch-btn"
            :class="{ 'dbv2-switch-btn--active': trafficSource === 'ga4' }"
            :aria-pressed="trafficSource === 'ga4'"
            @click="switchTrafficSource('ga4')"
          >
            GA4
          </button>

          <div
            class="dbv2-switch-indicator"
            :class="{
              'dbv2-switch-indicator--center': trafficSource === 'ga4',
            }"
          ></div>
        </div>
      </div>
    </div>

    <!-- Tab Content -->
    <v-tabs-items v-model="activeTab" class="dbv2-content">
      <v-tab-item>
        <dashboard-v2-overview :view="currentView" />
      </v-tab-item>
      <v-tab-item>
        <dashboard-v2-analytics :view="currentView" />
      </v-tab-item>
      <v-tab-item>
        <dashboard-v2-performance />
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import {
  mdiChartBoxOutline, mdiCalendarMonth, mdiCalendarText,
  mdiCalendarRange, mdiMagnify, mdiViewDashboardOutline,
  mdiChartLine, mdiTrophyOutline,
} from '@mdi/js'
import { computed, nextTick, onMounted, ref, watch } from '@vue/composition-api'
import {
  DEFAULT_COMPARISON,
  buildCacheOptions,
  buildKpiDateParams,
  buildScopeParams,
} from '@/store/dashboard-v2'
import { monthsFrArray, getDateStr } from '@/utils/dates'
import { formatCurrencyCompact, formatNumberCompact, formatCurrency, formatPercent } from '@/utils/formatting'
import useProxy from '@/hooks/useProxy'
import useAxios from '@/hooks/useAxios'
import RmrActionButton from '@/components/ui/actions/RmrActionButton.vue'
import RmrDatePickerField from '@/components/ui/forms/RmrDatePickerField.vue'
import RmrPageHero from '@/components/ui/layout/RmrPageHero.vue'
import RmrSelect from '@/components/ui/forms/RmrSelect.vue'
import RmrTabs from '@/components/ui/layout/RmrTabs.vue'
import DashboardV2Overview from '@/components/general/dashboard-v2/tabs/DashboardV2Overview.vue'
import DashboardV2Analytics from '@/components/general/dashboard-v2/tabs/DashboardV2Analytics.vue'
import DashboardV2Performance from '@/components/general/dashboard-v2/tabs/DashboardV2Performance.vue'
import V2PartnerViewGroupFilter from '@/v2/components/common/filters/PartnerViewGroupFilter.vue'
import V2SiteGroupFilter from '@/v2/components/common/filters/SiteGroupFilter.vue'
import V2SiteFilter from '@/v2/components/common/filters/SiteFilter.vue'
import { RMR_CA, RMR_GREEN, RMR_INDIGO_DARK, RMR_ON_DARK_MUTED, RMR_ORANGE, RMR_RPM, RMR_SESSIONS } from '@/styles/tokens'

const COMPONENT_NAME = 'DashboardV2'

const icons = {
  mdiChartBoxOutline, mdiCalendarMonth, mdiCalendarText,
  mdiCalendarRange, mdiMagnify, mdiViewDashboardOutline,
  mdiChartLine, mdiTrophyOutline,
}

export default {
  name: COMPONENT_NAME,
  components: { DashboardV2Overview, DashboardV2Analytics, DashboardV2Performance, V2PartnerViewGroupFilter, V2SiteGroupFilter, V2SiteFilter, RmrActionButton, RmrDatePickerField, RmrPageHero, RmrSelect, RmrTabs },
  setup() {
    const { store } = useProxy()
    const { axiosGet, isLoading } = useAxios()

    // => CONST
    const lastUpdate = ref(null)
    const currentView = ref('month')
    const activeTab = ref(0)
    const tabs = [
      { key: 'overview', label: "Vue d'ensemble", icon: icons.mdiViewDashboardOutline },
      { key: 'analytics', label: 'Analytique', icon: icons.mdiChartLine },
      { key: 'performance', label: 'Performance', icon: icons.mdiTrophyOutline },
    ]
    const dateMenuFrom = ref(false)
    const dateMenuRange = ref(false)
    const rangeAnchor = ref(null)
    const rangeDraft = ref([])
    const rangePickerKey = ref(0)

    // => METHODS
    const formatDateLabel = (dateStr) => {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
    }

    const setMonthDates = (year, monthIndex) => {
      store.commit('dashboardV2/setDateFrom', getDateStr(new Date(year, monthIndex, 1)))
      store.commit('dashboardV2/setDateTo', getDateStr(new Date(year, monthIndex + 1, 0)))
    }

    const setYearDates = (year) => {
      const now = new Date()
      const end = year === now.getFullYear() ? now : new Date(year, 11, 31)
      store.commit('dashboardV2/setDateFrom', getDateStr(new Date(year, 0, 1)))
      store.commit('dashboardV2/setDateTo', getDateStr(end))
    }

    const resetRangePickerState = () => {
      rangeAnchor.value = null
      rangeDraft.value = [dateFrom.value, dateTo.value]
      rangePickerKey.value += 1
    }

    const isRangeDateAllowed = (dateStr) => {
      if (dateStr > todayStr.value) return false
      if (!rangeAnchor.value) return true
      const anchor = new Date(`${rangeAnchor.value}T00:00:00`)
      const current = new Date(`${dateStr}T00:00:00`)
      const diffDays = Math.floor(Math.abs(current - anchor) / (24 * 60 * 60 * 1000))
      return diffDays <= 365
    }

    const onRangePicked = (values) => {
      if (!Array.isArray(values) || values.length === 0) return
      if (values.length < 2) {
        rangeAnchor.value = values[0] || null
        rangeDraft.value = values
        return
      }

      const sorted = [...values].filter(Boolean).sort()
      let start = sorted[0]
      let end = sorted[sorted.length - 1] || sorted[0]

      if (end > todayStr.value) end = todayStr.value

      const startDate = new Date(`${start}T00:00:00`)
      const endDate = new Date(`${end}T00:00:00`)
      const diffDays = Math.floor((endDate - startDate) / (24 * 60 * 60 * 1000))

      if (diffDays > 365) {
        const cappedEnd = new Date(startDate)
        cappedEnd.setDate(cappedEnd.getDate() + 365)
        end = getDateStr(cappedEnd)
      }

      store.commit('dashboardV2/setDateFrom', start)
      store.commit('dashboardV2/setDateTo', end)
      rangeAnchor.value = null
      rangeDraft.value = [start, end]

      if (values.length >= 2) {
        dateMenuRange.value = false
      }
    }

    const switchView = (view) => {
      currentView.value = view
      const now = new Date()
      if (view === 'month') {
        setMonthDates(now.getFullYear(), now.getMonth())
      } else if (view === 'year') {
        setYearDates(now.getFullYear())
      } else if (view === 'range') {
        resetRangePickerState()
      }
    }

    const onMonthPicked = (val) => {
      dateMenuFrom.value = false
      const [y, m] = val.split('-').map(Number)
      setMonthDates(y, m - 1)
    }

    const onYearPicked = (year) => {
      setYearDates(year)
    }

    const setDefaultFilters = async () => {
      // Skip if filters were already restored from URL by the V2 filter components
      if (localPartnerViewGroups.value.length > 0) return
      const exportGroup = partnerViewGroupList.value.find(
        (g) => g.name === 'Export Suivi Général'
      )
      if (exportGroup) {
        await store.dispatch('dashboardV2/updatePartnerViewGroups', [exportGroup.id])
      }
    }

    const removePartnerViewGroup = (id) => {
      const filtered = localPartnerViewGroups.value.filter((v) => v !== id)
      store.dispatch('dashboardV2/updatePartnerViewGroups', filtered)
    }

    const removeSiteGroup = (id) => {
      const filtered = localSiteGroups.value.filter((v) => v !== id)
      store.dispatch('dashboardV2/updateSiteGroups', filtered)
    }

    const removeSite = (id) => {
      const filtered = localSites.value.filter((v) => v !== id)
      store.dispatch('dashboardV2/updateSites', filtered)
    }

    const clearAllSelections = () => {
      store.dispatch('dashboardV2/clearFilters')
    }

    let latestRequestId = 0

    const fetchOverview = async (params, cacheOpts = {}, requestId = latestRequestId) => {
      try {
        const { data } = await axiosGet('/dashboard/kpi-summary', params, cacheOpts)
        if (requestId !== latestRequestId) return
        const currentCa = Number(data?.ca?.real || 0)
        const caObjective = Number(data?.ca?.objective || 0)
        const caEstimateRatio = Number(
          data?.ca?.estimate_ratio != null ? data?.ca?.estimate_ratio : data?.ca?.ratio || 0
        )
        const sessions = Number(data?.sessions?.real || 0)
        const sessObjective = Number(data?.sessions?.objective || 0)
        const rpm = Number(data?.rpm?.real || 0)
        const rpmObjective = Number(data?.rpm?.objective || 0)
        const caRatio = Number(data?.ca?.ratio || 0) * 100
        const sessRatio = Number(data?.sessions?.ratio || 0) * 100
        const rpmRatio = Number(data?.rpm?.ratio || 0) * 100

        store.commit('dashboardV2/setOverviewKpiData', {
          ca: {
            value: formatCurrencyCompact(currentCa), objective: formatCurrencyCompact(caObjective), projection: formatCurrencyCompact(currentCa),
            progress: Math.min(Math.round(caRatio), 100), ratio: parseFloat(caRatio.toFixed(1)),
            estimationRatio: formatPercent(caEstimateRatio, 1), comparison: { ...DEFAULT_COMPARISON },
          },
          sessions: {
            value: formatNumberCompact(sessions), objective: formatNumberCompact(sessObjective), projection: formatNumberCompact(sessions),
            progress: Math.min(Math.round(sessRatio), 100), ratio: parseFloat(sessRatio.toFixed(1)),
            comparison: { ...DEFAULT_COMPARISON },
          },
          rpm: {
            value: formatCurrency(rpm, 2), objective: formatCurrency(rpmObjective, 2), current: formatCurrency(rpm, 2),
            progress: Math.min(Math.round(rpmRatio), 100), ratio: parseFloat(rpmRatio.toFixed(1)),
            comparison: { ...DEFAULT_COMPARISON },
          },
        })
        store.commit('dashboardV2/setOverviewComparisonsLoaded', false)
      } catch (error) {
        console.error('Error fetching overview data:', error)
      }
    }

    const fetchAnalytics = async (view, baseParams, cacheOpts = {}, requestId = latestRequestId) => {
      try {
        const existing = store.getters['dashboardV2/getAnalyticsData']
        const limit = existing?.limit || 10
        const sortBy = existing?.sortBy || 'date'
        const sortDesc = Boolean(existing?.sortDesc)

        const [tableRes, seriesRes] = await Promise.all([
          axiosGet('/dashboard/daily-reporting', {
            ...baseParams, limit, page: 1, sort_by: sortBy, sort_desc: sortDesc,
          }, cacheOpts),
          axiosGet('/dashboard/daily-reporting-series', {
            ...baseParams, view,
          }, cacheOpts),
        ])
        if (requestId !== latestRequestId) return

        const seriesRows = seriesRes.data?.items || []
        const dates = [], caValues = [], caObjValues = [], sessValues = [], sessObjValues = [], rpmValues = []
        const yesterday = new Date()
        yesterday.setDate(yesterday.getDate() - 1)
        const yesterdayStr = getDateStr(yesterday)
        seriesRows.forEach((item) => {
          const ca = Number(item.ca_real || 0)
          const sess = Number(item.sessions || 0)
          // API fills days without data with 0: leave recent empty days blank instead of plotting a drop.
          const notLoadedYet = item.date >= yesterdayStr
          dates.push(item.date)
          caValues.push(notLoadedYet && !ca ? null : ca)
          caObjValues.push(Number(item.obj_ca || 0))
          sessValues.push(notLoadedYet && !sess ? null : sess)
          sessObjValues.push(Number(item.obj_sessions || 0))
          rpmValues.push(parseFloat((sess > 0 ? (ca / sess) * 1000 : 0).toFixed(2)))
        })

        const dailyData = (tableRes.data?.items || []).map((item) => ({
          date: item.date, sessions: Number(item.sessions || 0), sessObj: Number(item.obj_sessions || 0),
          ca: Number(item.ca_real || 0), caObj: Number(item.obj_ca || 0), rpm: Number(item.rpm || 0),
        }))

        store.commit('dashboardV2/setAnalyticsData', {
          dailyData, total: Number(tableRes.data?.total || 0),
          page: 1, limit, sortBy, sortDesc,
          dates, caValues, caObjValues, sessValues, sessObjValues, rpmValues,
        })
      } catch (error) {
        console.error('Error fetching analytics data:', error)
      }
    }

    const mapTopSites = (topRpmRes) => {
      const rItems = topRpmRes?.data?.items || topRpmRes?.data || []
      return Array.isArray(rItems) && rItems.length > 0
        ? rItems.slice(0, 10).map((item) => ({ name: item.site_name || item.name || '', rpm: item.rpm || 0 }))
        : []
    }

    const fetchTopRpmSites = async (sourceParams, cacheOpts = {}, requestId = latestRequestId) => {
      try {
        const topRpmRes = await axiosGet('/dashboard/top-rpm-per-site', { ...sourceParams, limit: 10 }, cacheOpts)
        if (requestId !== latestRequestId) return
        const existing = store.getters['dashboardV2/getPerformanceData'] || {}
        store.commit('dashboardV2/setPerformanceData', {
          ...existing,
          topSites: mapTopSites(topRpmRes),
        })
      } catch (error) {
        console.error('Error fetching top RPM per site:', error)
      }
    }

    const fetchPerformance = async (sourceParams, sourceIndependentParams, cacheOpts = {}, requestId = latestRequestId) => {
      try {
        const colors = [RMR_INDIGO_DARK, RMR_CA, RMR_GREEN, RMR_RPM, RMR_SESSIONS, RMR_ORANGE]
        const [partnersRes, topRpmRes, topArticlesRes, sourcesRes] = await Promise.all([
          axiosGet('/dashboard/best-partners', sourceIndependentParams, cacheOpts),
          axiosGet('/dashboard/top-rpm-per-site', { ...sourceParams, limit: 10 }, cacheOpts),
          axiosGet('/dashboard/top-articles', { ...sourceIndependentParams, limit: 10 }, cacheOpts),
          axiosGet('/dashboard/sources-distribution', sourceIndependentParams, cacheOpts),
        ])
        if (requestId !== latestRequestId) return

        const pItems = partnersRes.data?.items || partnersRes.data || []
        const partners = Array.isArray(pItems) && pItems.length > 0
          ? (() => {
              const total = pItems.reduce((sum, i) => sum + (i.ca || i.revenue || 0), 0)
              return pItems.slice(0, 6).map((item, idx) => ({
                name:    item.partner_name || item.name || `Partner ${idx + 1}`,
                value:   item.ca || item.revenue || 0,
                percent: total > 0 ? Math.round(((item.ca || item.revenue || 0) / total) * 100) : 0,
                color:   colors[idx % colors.length],
              }))
            })()
          : []

        const topSites = mapTopSites(topRpmRes)

        const aItems = topArticlesRes.data?.items || []
        const topArticles = Array.isArray(aItems) && aItems.length > 0
          ? aItems.map((item, idx) => ({
              rank: item.rank || idx + 1, title: item.title || '', category: item.category || '',
              sessions: Number(item.sessions || 0), ca: Number(item.ca || 0), rpm: Number(item.rpm || 0),
            }))
          : []

        const sItems = sourcesRes.data?.items || []
        const sources = Array.isArray(sItems) && sItems.length > 0
          ? sItems.map((item) => ({
              name: item.channel || '', percent: Number(item.percent || 0),
              sessions: Number(item.sessions || 0), ca: Number(item.ca || 0),
            }))
          : []

        store.commit('dashboardV2/setPerformanceData', { partners, topSites, topArticles, sources })
      } catch (error) {
        console.error('Error fetching performance data:', error)
      }
    }

    const buildFilterPayloads = () => {
      const state = store.state.dashboardV2
      const cacheOpts = buildCacheOptions(state.dateTo)
      const kpiParams = {
        ...buildKpiDateParams(currentView.value, state.dateFrom, state.dateTo),
        ...buildScopeParams(state, true),
      }
      const sourceParams = {
        date_start: state.dateFrom,
        date_end: state.dateTo,
        ...buildScopeParams(state, true),
      }
      const sourceIndependentParams = {
        date_start: state.dateFrom,
        date_end: state.dateTo,
        ...buildScopeParams(state, false),
      }
      return { cacheOpts, kpiParams, sourceParams, sourceIndependentParams }
    }

    const startRefresh = () => {
      store.commit('dashboardV2/incrementRefreshGeneration')
      const requestId = store.getters['dashboardV2/getRefreshGeneration']
      latestRequestId = requestId
      lastUpdate.value = new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
      return requestId
    }

    const switchTrafficSource = async (source) => {
      if (trafficSource.value === source) return
      store.commit('dashboardV2/setTrafficSource', source)
      const requestId = startRefresh()
      const { cacheOpts, kpiParams, sourceParams, sourceIndependentParams } = buildFilterPayloads()
      const tasks = [
        fetchOverview(kpiParams, cacheOpts, requestId),
        fetchAnalytics(currentView.value, sourceParams, cacheOpts, requestId),
        fetchTopRpmSites(sourceParams, cacheOpts, requestId),
      ]
      if (source === 'ga4') {
        tasks.push(fetchPerformance(sourceParams, sourceIndependentParams, cacheOpts, requestId))
      }
      await Promise.all(tasks)
    }

    const applyFilters = async () => {
      const requestId = startRefresh()
      const { cacheOpts, kpiParams, sourceParams, sourceIndependentParams } = buildFilterPayloads()
      await Promise.all([
        fetchOverview(kpiParams, cacheOpts, requestId),
        fetchAnalytics(currentView.value, sourceParams, cacheOpts, requestId),
        fetchPerformance(sourceParams, sourceIndependentParams, cacheOpts, requestId),
      ])
    }

    const onTabChange = () => {
      // ApexCharts renders with 0-width when its container is hidden (v-show).
      // Dispatching a resize event after the tab transition forces all charts to redraw.
      nextTick(() => window.dispatchEvent(new Event('resize')))
    }

    // => COMPUTED
    const trafficSource = computed(() => store.getters['dashboardV2/getTrafficSource'])
    const dateFrom = computed(() => store.getters['dashboardV2/getDateFrom'])
    const dateTo = computed(() => store.getters['dashboardV2/getDateTo'])
    const localPartnerViewGroups = computed(() => store.getters['dashboardV2/getPartnerViewGroups'])
    const localSiteGroups = computed(() => store.getters['dashboardV2/getSiteGroups'])
    const localSites = computed(() => store.getters['dashboardV2/getSites'])
    const siteGroupList = computed(() => store.getters['common/getSiteGroupList'])
    const siteList = computed(() => store.getters['common/getSiteList'])
    const partnerViewGroupList = computed(() => store.getters['common/getPartnerViewGroupList'])
    const todayStr = computed(() => getDateStr(new Date()))
    const formattedDateFrom = computed(() => formatDateLabel(dateFrom.value))
    const formattedDateTo = computed(() => formatDateLabel(dateTo.value))
    const selectedMonthPicker = computed({
      get: () => (dateFrom.value ? dateFrom.value.substring(0, 7) : ''),
      set: () => {},
    })
    const selectedYear = computed(() =>
      dateFrom.value ? new Date(dateFrom.value).getFullYear() : new Date().getFullYear()
    )
    const yearList = computed(() => {
      const current = new Date().getFullYear()
      const years = []
      for (let y = current; y >= current - 5; y--) years.push(y)
      return years
    })
    const periodLabel = computed(() => {
      if (currentView.value === 'month') {
        const d = new Date(dateFrom.value)
        return `${monthsFrArray[d.getMonth()]} ${d.getFullYear()}`
      }
      if (currentView.value === 'year') {
        return `Année ${new Date(dateFrom.value).getFullYear()}`
      }
      return `${formattedDateFrom.value} — ${formattedDateTo.value}`
    })
    const selectedPartnerGroupChips = computed(() =>
      partnerViewGroupList.value.filter((g) => localPartnerViewGroups.value.includes(g.id))
    )
    const selectedSiteGroupChips = computed(() =>
      siteGroupList.value.filter((g) => localSiteGroups.value.includes(g.id))
    )
    const selectedSiteChips = computed(() =>
      siteList.value.filter((s) => localSites.value.includes(s.id))
    )
    const hasActiveSelections = computed(() =>
      localPartnerViewGroups.value.length > 0 || localSiteGroups.value.length > 0 || localSites.value.length > 0
    )
    const totalSelections = computed(() =>
      localPartnerViewGroups.value.length + localSiteGroups.value.length + localSites.value.length
    )

    // => WATCH
    watch(currentView, (view) => {
      if (view === 'range') {
        resetRangePickerState()
      }
    })

    watch(dateMenuRange, (open) => {
      if (open) {
        resetRangePickerState()
      } else {
        rangeAnchor.value = null
      }
    })

    // => HOOKS
    onMounted(async () => {
      // Needed for setDefaultFilters + chip label resolution; V2 filter components
      // also fetch these independently, so the calls are idempotent.
      await store.dispatch('common/getPartnerViewGroupList')
      await setDefaultFilters()
      applyFilters()
    })

    // => DATA
    return {
      icons,
      trafficSource,
      currentView,
      activeTab,
      tabs,
      dateMenuFrom,
      dateMenuRange,
      rangeAnchor,
      rangeDraft,
      rangePickerKey,
      dateFrom,
      dateTo,
      localPartnerViewGroups,
      localSiteGroups,
      localSites,
      isLoading,
      lastUpdate,
      periodLabel,
      selectedMonthPicker,
      selectedYear,
      yearList,
      formattedDateFrom,
      formattedDateTo,
      todayStr,
      selectedPartnerGroupChips,
      selectedSiteGroupChips,
      selectedSiteChips,
      hasActiveSelections,
      totalSelections,
      isRangeDateAllowed,
      onRangePicked,
      switchTrafficSource,
      switchView,
      onMonthPicked,
      onYearPicked,
      RMR_INDIGO_DARK,
      RMR_ON_DARK_MUTED,
      removePartnerViewGroup,
      removeSiteGroup,
      removeSite,
      clearAllSelections,
      applyFilters,
      onTabChange,
    }
  },
}
</script>

<style lang="scss" scoped>
.dbv2 {
  background: #F1F5F9;
  min-height: 100vh;
  padding-bottom: 48px;
}

.dbv2-update-line {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin: 4px 0 0;
  font-weight: 500;
}

.dbv2-update {
  color: rgba(255, 255, 255, 0.35);
}

.dbv2-hero-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Month/Year/Range & Traffic Source Switch */
.dbv2-switch {
  position: relative;
  display: flex;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 4px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  gap: 2px;

  &--two {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
}

.dbv2-switch-btn {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 20px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
  border-radius: 9px;
  transition: color 0.3s ease;
  white-space: nowrap;

  &--active {
    color: $rmr-indigo-dark;
  }
}

.dbv2-switch-indicator {
  position: absolute;
  top: 4px;
  left: 4px;
  width: calc(50% - 5px);
  height: calc(100% - 8px);
  background: #FFFFFF;
  border-radius: 9px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);

  &--three {
    width: calc(33.33% - 4px);
  }

  &--center {
    transform: translateX(calc(100% + 2px));
  }

  &--right {
    transform: translateX(calc(200% + 4px));
  }
}

/* Filters Bar */
.dbv2-filters {
  max-width: 1440px;
  margin: -16px auto 0;
  padding: 0 24px;
  position: relative;
  z-index: 5;
}

.dbv2-filters-card {
  border-radius: 14px !important;
  padding: 16px 20px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04) !important;
}

.dbv2-filters-row {
  display: flex;
  gap: 12px;
  align-items: flex-end;
  flex-wrap: wrap;
}

.dbv2-filter-item {
  min-width: 140px;
}

.dbv2-filter-item--range {
  flex: 0 0 320px;
  min-width: 320px;
}

.dbv2-filter-grow {
  flex: 1;
  min-width: 200px;
}

.dbv2-filter-action {
  min-width: auto;
  flex: 0;
}

.dbv2-filter-label {
  display: block;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: $rmr-text-subtle;
  margin-bottom: 6px;
}

.dbv2-selection-count {
  font-size: 12px;
  font-weight: 600;
  color: $rmr-indigo;
  white-space: nowrap;
}

/* Active Chips Row */
.dbv2-chips-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding-top: 12px;
  margin-top: 12px;
  border-top: 1px solid #F1F5F9;
}

.dbv2-chips-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: $rmr-text-subtle;
  white-space: nowrap;
  padding-top: 4px;
}

.dbv2-chips-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

.dbv2-chip {
  font-weight: 600 !important;
  font-size: 11px !important;
  height: 26px !important;
  border-radius: 8px !important;

  &--partner {
    background: $rmr-indigo-bg !important;
    color: $rmr-indigo-accent !important;

    ::v-deep .v-chip__close { color: $rmr-indigo-accent !important; }
  }

  &--sitegroup {
    background: #F0FDFA !important;
    color: #0F766E !important;

    ::v-deep .v-chip__close { color: #0F766E !important; }
  }

  &--site {
    background: #FFF7ED !important;
    color: #C2410C !important;

    ::v-deep .v-chip__close { color: #C2410C !important; }
  }
}

.dbv2-chips-clear {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: $rmr-danger;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: background 0.15s;

  &:hover {
    background: #FEE2E2;
  }
}

/* Tab Navigation */
.dbv2-nav {
  max-width: 1440px;
  margin: 24px auto 0;
  padding: 0 24px;
}

.dbv2-nav-bar {
  position: relative;
  display: flex;
  align-items: center;

  ::v-deep .v-tabs-bar {
    padding-right: 180px;
  }
}

.dbv2-switch--nav {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  background: #E2E8F0;
  border: 1px solid #CBD5E1;
  border-radius: 10px;
  padding: 3px;

  .dbv2-switch-btn {
    padding: 6px 16px;
    font-size: 11px;
    color: $rmr-text-muted;
    border-radius: 7px;

    &:hover:not(.dbv2-switch-btn--active) {
      color: $rmr-text-base;
    }

    &--active {
      color: $rmr-indigo-dark;
    }
  }

  .dbv2-switch-indicator {
    top: 3px;
    left: 3px;
    width: calc(50% - 4px);
    height: calc(100% - 6px);
    border-radius: 7px;
    box-shadow: 0 1px 4px rgba(15, 23, 42, 0.12);
  }
}

/* Tab Content */
.dbv2-content {
  max-width: 1440px;
  margin: 0 auto;
  padding: 24px 24px 0;
  background: transparent !important;

  ::v-deep .v-window-item {
    background: transparent;
  }
}

/* Responsive */
@media (max-width: 960px) {
  .dbv2-filters {
    padding: 0 12px;
  }

  .dbv2-filters-row {
    flex-direction: column;
  }

  .dbv2-filter-item {
    width: 100%;
    min-width: 100%;
  }

  .dbv2-nav {
    padding: 0 12px;
  }

  .dbv2-nav-bar {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;

    ::v-deep .v-tabs-bar {
      padding-right: 0;
    }
  }

  .dbv2-switch--nav {
    position: static;
    transform: none;
    align-self: flex-end;
  }

  .dbv2-content {
    padding: 16px 12px 0;
  }
}
</style>
